/**
 * Código GFIP e Enquadramento da Previdência Social, com a faixa que explica de onde veio o
 * código (#302).
 *
 * O componente não calcula nem decide: recebe o estado do cálculo e o aviso de divergência
 * prontos e devolve à página o que a pessoa fez. Os campos nunca são desabilitados — enquanto
 * calcula, sem cargo, sem acesso ou em falha, quem sabe o código pode digitá-lo.
 */

import React from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Grid, Link, MenuItem, TextField, Typography } from '@mui/material';
import { Controller, type Control } from 'react-hook-form';

import type { AposentadoriaEspecialDoCargo, RiscoDoCargo } from '../../../services/efetivosService';
import type { EstadoDoCalculo } from '../../../hooks/useAposentadoriaEspecialDoCargo';
import {
  SOCIAL_SECURITY_CLASSIFICATION_LABELS,
  codigoDoEnquadramento,
  type CodigoAposentadoriaEspecial,
  type EmployeeFormData,
} from '../../../types/employee';
import type { AvisoDeDivergencia, CampoAposentadoria } from '../aposentadoriaEspecial';

/** Contribuição extra da empresa por código (Tabela 2 do S-1200); o código 1 não tem. */
const CONTRIBUICAO_EXTRA_POR_CODIGO: Record<CodigoAposentadoriaEspecial, string | null> = {
  1: null,
  2: '12%',
  3: '9%',
  4: '6%',
};

const ROTA_DE_RISCOS_APLICADOS = '/risk-applications';
const ROTA_DO_CATALOGO = '/occupational-risks';

/** Corpo da faixa em 16px: quem lê com pouca intimidade com tecnologia não deve precisar de lupa. */
const CORPO_DA_FAIXA = { fontSize: 16, lineHeight: 1.5 } as const;
const BOTAO_GRANDE = { minHeight: 44, fontSize: 16 } as const;

export interface AposentadoriaEspecialFieldsProps {
  control: Control<EmployeeFormData>;
  estado: EstadoDoCalculo;
  aviso: AvisoDeDivergencia;
  /** Valores atuais dos dois campos, para a faixa dizer qual deles não bate. */
  gfip: string;
  enquadramento: EmployeeFormData['socialSecurityClassification'];
  /** A pessoa digitou ou escolheu no campo: o cálculo não o sobrescreve mais. */
  onEditar: (campo: CampoAposentadoria) => void;
  onUsarCodigo: (codigo: CodigoAposentadoriaEspecial) => void;
  onManter: () => void;
  onTentarDeNovo: () => void;
}

function listaDeNomes(nomes: string[]): string {
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

function verboDoPlural(quantidade: number): string {
  return quantidade > 1 ? 'dão' : 'dá';
}

function efeitoNaContribuicao(codigoDaTela: CodigoAposentadoriaEspecial, codigoCalculado: CodigoAposentadoriaEspecial): string {
  const taxaDaTela = CONTRIBUICAO_EXTRA_POR_CODIGO[codigoDaTela];
  const taxaCalculada = CONTRIBUICAO_EXTRA_POR_CODIGO[codigoCalculado];
  const daTela = taxaDaTela ? `a contribuição extra da empresa é de ${taxaDaTela}` : 'não há contribuição extra da empresa';
  const calculada = taxaCalculada ? `de ${taxaCalculada}` : 'não há contribuição extra';
  return ` No código ${codigoDaTela}, ${daTela}; no ${codigoCalculado}, ${calculada}.`;
}

function codigoDigitado(gfip: string): CodigoAposentadoriaEspecial | null {
  const texto = gfip.trim();
  return /^[1-4]$/.test(texto) ? (Number(texto) as CodigoAposentadoriaEspecial) : null;
}

function AcaoDeLink({ href, children }: { href: string; children: string }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      color="inherit"
      underline="always"
      aria-label={`${children} (abre em outra aba)`}
      sx={{ ...CORPO_DA_FAIXA, display: 'inline-flex', alignItems: 'center', minHeight: 44 }}
    >
      {children}
    </Link>
  );
}

function Faixa({
  severity,
  titulo,
  children,
  icon,
  acoes,
}: {
  severity: 'success' | 'warning' | 'info';
  titulo: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  acoes?: React.ReactNode;
}) {
  return (
    <Alert severity={severity} role="status" icon={icon} sx={{ mt: 2, alignItems: 'flex-start', ...CORPO_DA_FAIXA }}>
      <AlertTitle sx={{ fontSize: 16, fontWeight: 600 }}>{titulo}</AlertTitle>
      {children ? <Typography sx={CORPO_DA_FAIXA}>{children}</Typography> : null}
      {acoes ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, alignItems: 'center' }}>{acoes}</Box> : null}
    </Alert>
  );
}

function FaixaDoResultado({
  resposta,
  aviso,
  gfip,
  enquadramento,
  onUsarCodigo,
  onManter,
}: {
  resposta: AposentadoriaEspecialDoCargo;
  aviso: AvisoDeDivergencia;
  gfip: string;
  enquadramento: EmployeeFormData['socialSecurityClassification'];
  onUsarCodigo: (codigo: CodigoAposentadoriaEspecial) => void;
  onManter: () => void;
}) {
  const verRiscos = <AcaoDeLink href={ROTA_DE_RISCOS_APLICADOS}>Ver riscos do cargo</AcaoDeLink>;

  if (resposta.situacao === 'sem-riscos') {
    return (
      <Faixa
        severity="info"
        titulo="Este cargo ainda não tem riscos cadastrados"
        acoes={<AcaoDeLink href={ROTA_DE_RISCOS_APLICADOS}>Cadastrar riscos do cargo</AcaoDeLink>}
      >
        Sem eles o sistema não calcula o código. Informe o código nos campos acima ou cadastre os riscos do cargo.
      </Faixa>
    );
  }

  if (resposta.situacao === 'tempos-diferentes') {
    const grupos = agruparPorAnos(resposta.riscos);
    const frases = grupos.map(
      (grupo) => `${listaDeNomes(grupo.nomes)} ${verboDoPlural(grupo.nomes.length)} ${grupo.anos} anos`
    );
    return (
      <Faixa severity="info" titulo="Os riscos deste cargo têm tempos diferentes" acoes={verRiscos}>
        {`${listaDeNomes(frases)}. O sistema não escolhe entre eles: informe o código nos campos acima.`}
      </Faixa>
    );
  }

  if (resposta.situacao === 'risco-sem-tempo') {
    const semTempo = resposta.riscos.filter((risco) => risco.anos === null);
    const catalogo = <AcaoDeLink href={ROTA_DO_CATALOGO}>Completar no catálogo</AcaoDeLink>;
    if (semTempo.length > 0) {
      const nomes = semTempo.map((risco) => risco.nome);
      return (
        <Faixa
          severity="info"
          titulo={
            semTempo.length === 1
              ? 'Um risco do cargo está sem tempo de aposentadoria'
              : 'Riscos do cargo estão sem tempo de aposentadoria'
          }
          acoes={catalogo}
        >
          {`${listaDeNomes(nomes)} ${semTempo.length === 1 ? 'não tem' : 'não têm'} o tempo no catálogo, então o sistema não calcula o código. Informe-o nos campos acima ou complete o catálogo.`}
        </Faixa>
      );
    }
    const comAnos = resposta.riscos.map((risco) => `${risco.nome} (${risco.anos} anos)`);
    return (
      <Faixa severity="info" titulo="O tempo de um risco do cargo não consta da tabela" acoes={catalogo}>
        {`${listaDeNomes(comAnos)}. O sistema não calcula o código com esse tempo. Informe-o nos campos acima ou corrija o catálogo.`}
      </Faixa>
    );
  }

  // Calculado: a partir daqui há código.
  const codigo = resposta.codigo;
  if (codigo === null) return null;

  const nomes = listaDeNomes(resposta.riscos.map((risco) => risco.nome));
  const quantidade = resposta.riscos.length;
  const motivo =
    codigo === 1
      ? `${nomes} não ${verboDoPlural(quantidade)} direito a aposentadoria especial, que é o código 1: não ensejador.`
      : `${nomes} ${verboDoPlural(quantidade)} ${resposta.anos} anos de atividade especial, que é o código ${codigo}: ensejador de aposentadoria especial.`;

  if (aviso.divergentes.length === 0) {
    return (
      <Faixa severity="success" titulo="Calculado pelos riscos do cargo" acoes={verRiscos}>
        {motivo}
      </Faixa>
    );
  }

  if (aviso.mantido) {
    return (
      <Faixa
        severity="info"
        titulo="Mantido como você informou"
        acoes={
          <Button
            variant="text"
            size="large"
            color="inherit"
            sx={{ ...BOTAO_GRANDE, textDecoration: 'underline', textTransform: 'none' }}
            onClick={() => onUsarCodigo(codigo)}
          >
            {`Usar código ${codigo}`}
          </Button>
        }
      >
        {`Diverge dos riscos do cargo, que dão o código ${codigo}.`}
      </Faixa>
    );
  }

  return (
    <FaixaDeDivergencia
      codigo={codigo}
      motivo={motivo}
      aviso={aviso}
      gfip={gfip}
      enquadramento={enquadramento}
      onUsarCodigo={onUsarCodigo}
      onManter={onManter}
    />
  );
}

function agruparPorAnos(riscos: RiscoDoCargo[]): Array<{ anos: number | null; nomes: string[] }> {
  const grupos = new Map<number | null, string[]>();
  for (const risco of riscos) {
    grupos.set(risco.anos, [...(grupos.get(risco.anos) ?? []), risco.nome]);
  }
  return [...grupos.entries()].map(([anos, nomes]) => ({ anos, nomes }));
}

function FaixaDeDivergencia({
  codigo,
  motivo,
  aviso,
  onUsarCodigo,
  onManter,
  gfip,
  enquadramento,
}: {
  codigo: CodigoAposentadoriaEspecial;
  motivo: string;
  aviso: AvisoDeDivergencia;
  onUsarCodigo: (codigo: CodigoAposentadoriaEspecial) => void;
  onManter: () => void;
  gfip: string;
  enquadramento: EmployeeFormData['socialSecurityClassification'];
}) {
  const doEnquadramento = codigoDoEnquadramento(enquadramento);
  const descricoes = aviso.divergentes.map((campo) => {
    if (campo === 'gfip') return gfip.trim() ? `o código GFIP ${gfip.trim()}` : 'o código GFIP em branco';
    return doEnquadramento ? `o Enquadramento ${doEnquadramento}` : 'o Enquadramento em branco';
  });
  const lista = listaDeNomes(descricoes);
  const titulo = `${lista.charAt(0).toUpperCase()}${lista.slice(1)} ${aviso.divergentes.length > 1 ? 'não batem' : 'não bate'} com os riscos do cargo`;

  // O efeito vem do código que a pessoa pôs, quando ele é um dos quatro. Texto livre ("004", "4-")
  // não é código: a faixa diz isso e o efeito, se couber, vem do Enquadramento.
  const gfipDivergente = aviso.divergentes.includes('gfip');
  const gfipForaDoFormato = gfipDivergente && gfip.trim() !== '' && codigoDigitado(gfip) === null;
  const formato = gfipForaDoFormato
    ? ` O valor digitado no GFIP não está no formato esperado (só o número de 1 a 4). O código dos riscos é ${codigo}.`
    : '';
  const codigoDaTela =
    (gfipDivergente ? codigoDigitado(gfip) : null) ??
    (aviso.divergentes.includes('enquadramento') ? doEnquadramento : null);
  const efeito = codigoDaTela ? efeitoNaContribuicao(codigoDaTela, codigo) : '';

  return (
    <Faixa
      severity="warning"
      titulo={titulo}
      acoes={
        <>
          <Button variant="contained" size="large" sx={BOTAO_GRANDE} onClick={() => onUsarCodigo(codigo)}>
            {`Usar código ${codigo}`}
          </Button>
          <Button
            variant="text"
            size="large"
            color="inherit"
            sx={{ ...BOTAO_GRANDE, textDecoration: 'underline', textTransform: 'none' }}
            onClick={onManter}
          >
            Manter como está
          </Button>
        </>
      }
    >
      {`${motivo}${formato}${efeito}`}
    </Faixa>
  );
}

function FaixaDoEstado({
  estado,
  aviso,
  gfip,
  enquadramento,
  onUsarCodigo,
  onManter,
  onTentarDeNovo,
}: Pick<
  AposentadoriaEspecialFieldsProps,
  'estado' | 'aviso' | 'gfip' | 'enquadramento' | 'onUsarCodigo' | 'onManter' | 'onTentarDeNovo'
>) {
  switch (estado.tipo) {
    case 'sem-cargo':
      return (
        <Faixa severity="info" titulo="Escolha o cargo acima">
          O sistema calcula o código pelos riscos do cargo. Se preferir, informe o código agora nos campos acima.
        </Faixa>
      );
    case 'calculando':
      return <Faixa severity="info" titulo="Calculando pelos riscos do cargo…" icon={<CircularProgress size={20} />} />;
    case 'sem-acesso':
      return (
        <Faixa severity="info" titulo="Você não tem acesso aos riscos deste cargo">
          Por isso o sistema não calcula o código. Informe-o nos campos acima.
        </Faixa>
      );
    case 'falha':
      return (
        <Faixa
          severity="warning"
          titulo="Não deu para calcular o código agora"
          acoes={
            <Button variant="outlined" size="large" sx={BOTAO_GRANDE} onClick={onTentarDeNovo}>
              Tentar de novo
            </Button>
          }
        >
          Informe-o nos campos acima ou tente de novo.
        </Faixa>
      );
    case 'resposta':
      return (
        <FaixaDoResultado
          resposta={estado.resposta}
          aviso={aviso}
          gfip={gfip}
          enquadramento={enquadramento}
          onUsarCodigo={onUsarCodigo}
          onManter={onManter}
        />
      );
  }
}

export const AposentadoriaEspecialFields: React.FC<AposentadoriaEspecialFieldsProps> = ({
  control,
  estado,
  aviso,
  gfip,
  enquadramento,
  onEditar,
  onUsarCodigo,
  onManter,
  onTentarDeNovo,
}) => {
  const divergeGfip = aviso.aberta && aviso.divergentes.includes('gfip');
  const divergeEnquadramento = aviso.aberta && aviso.divergentes.includes('enquadramento');

  return (
    <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Aposentadoria especial
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          define a contribuição extra da empresa
        </Typography>
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="esocial.gfip"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Código GFIP"
                fullWidth
                size="small"
                color={divergeGfip ? 'warning' : undefined}
                focused={divergeGfip || undefined}
                slotProps={{ htmlInput: { maxLength: 20, inputMode: 'numeric', autoComplete: 'off' } }}
                onChange={(evento) => {
                  field.onChange(evento);
                  onEditar('gfip');
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Controller
            name="socialSecurityClassification"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Enquadramento da Previdência Social"
                fullWidth
                size="small"
                color={divergeEnquadramento ? 'warning' : undefined}
                focused={divergeEnquadramento || undefined}
                onChange={(evento) => {
                  field.onChange(evento);
                  onEditar('enquadramento');
                }}
              >
                <MenuItem value="">Selecione...</MenuItem>
                {Object.entries(SOCIAL_SECURITY_CLASSIFICATION_LABELS).map(([chave, rotulo]) => (
                  <MenuItem key={chave} value={chave}>
                    {rotulo}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </Grid>

      <Box>
        <FaixaDoEstado
          estado={estado}
          aviso={aviso}
          gfip={gfip}
          enquadramento={enquadramento}
          onUsarCodigo={onUsarCodigo}
          onManter={onManter}
          onTentarDeNovo={onTentarDeNovo}
        />
      </Box>
    </Box>
  );
};

export default AposentadoriaEspecialFields;
